import { T, useGT } from 'gt-react';
import { Card } from 'src/components/ui/card';
import { toast } from 'src/components/ui/toast';
import { cn } from '@/lib/utils';
import type { CommentResponse } from '@erp/rpc';
import { NoteComposer } from './NoteComposer';
import { NoteRow } from './NoteRow';
import type { MentionCandidate } from './mention-input';

/**
 * Compact free-text notes on a record, with an inline composer.
 *
 * Extracted from `TaskCommentsCard` so a page whose front door is NOT the task
 * can render the same thread: a pricelist update case comments against its
 * companion task, but through its own procedures, authorized as the CASE
 * (`Subject.Product`) rather than by proxying `Task:Read`. The data
 * access is therefore the caller's; only the rendering and the composer live
 * here. Two copies would have drifted the moment either gained attachments.
 *
 * For any record type in `COMMENTABLE_ENTITY_TYPES`, prefer
 * `EntityNotesPanel`, which binds this card to the generic `comments.*`
 * procedures and turns on @mentions.
 */

export interface CommentsCardProps {
  comments: CommentResponse[];
  isLoading?: boolean;
  /** The thread could not be fetched; shown instead of an empty thread. */
  isError?: boolean;
  /** Posts a new comment. Throwing surfaces an error toast. */
  onSubmit: (body: string, options?: { internal: boolean }) => Promise<void>;
  isSubmitting?: boolean;
  /** Hide the composer: the viewer may read the thread but not post. */
  readOnly?: boolean;
  /** Offer an "Internal note" toggle; internal notes carry a chip. */
  allowInternalNotes?: boolean;
  /**
   * Heading above the thread. Pass `null` when the host already labels the
   * thread (e.g. a tab); the count then belongs to the host too.
   */
  heading: React.ReactNode | null;
  /** Name shown for agent-authored comments. */
  agentName?: string;
  /** Lets the current user's own comments read as "You". */
  currentUserId?: string;
  userNameById?: Map<string, string>;
  /**
   * People the composer offers after `@`. Omit to leave mentions off — the
   * server only notifies users who can read the record, so pass the list the
   * record's own `listMentionableUsers` returns.
   */
  mentionableUsers?: readonly MentionCandidate[];
  onUpdate?: (commentId: string, body: string) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
  className?: string;
}

/** Author and mention names; the org list wins over the mentionable list. */
function mergeUserNames(
  userNameById: Map<string, string> | undefined,
  mentionableUsers: readonly MentionCandidate[] | undefined,
): Map<string, string> {
  const merged = new Map(userNameById);
  for (const user of mentionableUsers ?? []) {
    if (!merged.has(user.id)) merged.set(user.id, user.name);
  }
  return merged;
}

export function CommentsCard({
  comments,
  isLoading = false,
  isError = false,
  onSubmit,
  isSubmitting = false,
  readOnly = false,
  allowInternalNotes = false,
  heading,
  agentName,
  currentUserId,
  userNameById,
  mentionableUsers,
  onUpdate,
  onDelete,
  className,
}: CommentsCardProps) {
  const gt = useGT();

  const nameById = mergeUserNames(userNameById, mentionableUsers);

  // Newest first: a note thread is read from the top, and the composer is
  // immediately beneath the heading rather than after a long scroll.
  const ordered = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  async function handleSubmit(
    body: string,
    internal = false,
  ): Promise<boolean> {
    try {
      // Callers without internal notes keep the one-argument call shape.
      if (allowInternalNotes) await onSubmit(body, { internal });
      else await onSubmit(body);
      toast.success(
        gt('Comment added', {
          context: 'toast after posting a comment on a record',
        }),
      );
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to add comment', {
              context: 'toast after posting a comment failed',
            }),
      );
      return false;
    }
  }

  return (
    <Card
      className={cn('flex min-h-0 flex-col overflow-hidden p-2.5', className)}
      data-testid="comments-card"
    >
      {heading !== null && (
        <div className="mb-1.5 flex shrink-0 items-center gap-1.5 text-[11px] font-semibold tracking-[0.06em] text-stone-500 uppercase">
          {heading}
          {/* Count only once there is a thread — "Notes 0" is noise. */}
          {!isLoading && comments.length > 0 && (
            <span
              className="rounded-full bg-stone-200 px-1.5 py-px text-[10px] font-semibold tabular-nums text-stone-500"
              data-testid="comments-count"
            >
              {comments.length}
            </span>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <NoteThread
          comments={ordered}
          isLoading={isLoading}
          isError={isError}
          agentName={agentName}
          currentUserId={currentUserId}
          userNameById={nameById}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      </div>

      {!readOnly && (
        <NoteComposer
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          mentionableUsers={mentionableUsers}
          allowInternalNotes={allowInternalNotes}
        />
      )}
    </Card>
  );
}

const THREAD_STATUS_CLASS = 'px-0.5 py-1 text-[11px] text-muted-foreground';

function NoteThread({
  comments,
  isLoading,
  isError,
  agentName,
  currentUserId,
  userNameById,
  onUpdate,
  onDelete,
}: {
  comments: CommentResponse[];
  isLoading: boolean;
  isError: boolean;
  agentName?: string;
  currentUserId?: string;
  userNameById: Map<string, string>;
  onUpdate?: (commentId: string, body: string) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
}) {
  if (isLoading) {
    return (
      <p className={THREAD_STATUS_CLASS}>
        <T context="loading state for a record's comments">Loading notes…</T>
      </p>
    );
  }
  if (isError) {
    return (
      <p className={cn(THREAD_STATUS_CLASS, 'text-destructive')} role="alert">
        <T context="error state when a record's comments could not be loaded">
          Couldn't load notes.
        </T>
      </p>
    );
  }
  if (comments.length === 0) {
    return (
      <p className={THREAD_STATUS_CLASS}>
        <T context="empty state for a record's comments">No notes yet.</T>
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {comments.map((comment) => (
        <NoteRow
          key={comment.id}
          comment={comment}
          agentName={agentName}
          currentUserId={currentUserId}
          userNameById={userNameById}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}
